import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import ThemeSwitcher from '../components/ThemeSwitcher';
import UserProfile from '../components/UserProfile';
import { ModernIcons } from '../components/ModernIcons';
import './HomePage.modern.css';

const HomePage: React.FC = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [greeting, setGreeting] = useState('');

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    const hour = new Date().getHours();
    if (hour < 12) {
      setGreeting('Доброе утро');
    } else if (hour < 18) {
      setGreeting('Добрый день');
    } else {
      setGreeting('Добрый вечер');
    }

    return () => clearInterval(timer);
  }, []);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('ru-RU', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen" style={{backgroundColor: '#f9fafb'}}>
      <div style={{maxWidth: '1280px', margin: '0 auto', padding: '32px 16px'}}>
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{marginBottom: '32px'}}
        >
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
            <div>
              <h1 style={{fontSize: '1.875rem', fontWeight: '700', color: '#111827', margin: '0'}}>{greeting}</h1>
              <p style={{color: '#6b7280', marginTop: '4px', margin: '4px 0 0 0'}}>Ваш персональный планер для гармоничной жизни</p>
            </div>
            
            <div style={{textAlign: 'right'}}>
              <div style={{fontSize: '1.5rem', fontFamily: 'monospace', color: '#111827'}}>{formatTime(currentTime)}</div>
              <div style={{fontSize: '0.875rem', color: '#6b7280'}}>{formatDate(currentTime)}</div>
            </div>
          </div>
        </motion.div>

        {/* Settings */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          style={{marginBottom: '32px'}}
        >
          <div className="flex gap-4 justify-end">
            <UserProfile />
            <ThemeSwitcher />
          </div>
        </motion.div>
        
        {/* Welcome Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-12"
        >
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Начните свой путь к лучшей версии себя
            </h2>
            <p className="text-gray-600 leading-relaxed">
              Современный инструмент для организации жизни, отслеживания здоровья и личностного развития. 
              Создан с учетом потребностей активных людей, стремящихся к гармонии и продуктивности.
            </p>
          </div>
        </motion.div>

        {/* Quick Access Buttons */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Link to="/planner" className="group">
              <div className="bg-white border border-gray-200 rounded-xl p-6 transition-all duration-300" 
                   style={{boxShadow: '0 2px 15px -3px rgba(0, 0, 0, 0.07), 0 10px 20px -2px rgba(0, 0, 0, 0.04)'}}
                   onMouseEnter={(e) => {
                     e.currentTarget.style.boxShadow = '0 10px 40px -10px rgba(0, 0, 0, 0.1), 0 2px 10px -2px rgba(0, 0, 0, 0.04)';
                     e.currentTarget.style.borderColor = '#d1d5db';
                     e.currentTarget.style.transform = 'translateY(-4px)';
                   }}
                   onMouseLeave={(e) => {
                     e.currentTarget.style.boxShadow = '0 2px 15px -3px rgba(0, 0, 0, 0.07), 0 10px 20px -2px rgba(0, 0, 0, 0.04)';
                     e.currentTarget.style.borderColor = '#e5e7eb';
                     e.currentTarget.style.transform = 'translateY(0)';
                   }}>
                <div className="flex items-center justify-between mb-3">
                  <div className="p-3 rounded-lg transition-colors" style={{backgroundColor: '#eff6ff'}} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dbeafe'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#eff6ff'}>
                    <div style={{color: '#2563eb'}}><ModernIcons.Calendar size={24} /></div>
                  </div>
                  <div style={{color: '#9ca3af'}} className="transition-colors" onMouseEnter={(e) => e.currentTarget.style.color = '#4b5563'} onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}><ModernIcons.ChevronRight size={20} /></div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Планирование</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Организуйте задачи, ставьте цели и отслеживайте прогресс
                </p>
              </div>
            </Link>

            <Link to="/health" className="group">
              <div className="bg-white border border-gray-200 rounded-xl p-6 transition-all duration-300" 
                   style={{boxShadow: '0 2px 15px -3px rgba(0, 0, 0, 0.07), 0 10px 20px -2px rgba(0, 0, 0, 0.04)'}}
                   onMouseEnter={(e) => {
                     e.currentTarget.style.boxShadow = '0 10px 40px -10px rgba(0, 0, 0, 0.1), 0 2px 10px -2px rgba(0, 0, 0, 0.04)';
                     e.currentTarget.style.borderColor = '#d1d5db';
                     e.currentTarget.style.transform = 'translateY(-4px)';
                   }}
                   onMouseLeave={(e) => {
                     e.currentTarget.style.boxShadow = '0 2px 15px -3px rgba(0, 0, 0, 0.07), 0 10px 20px -2px rgba(0, 0, 0, 0.04)';
                     e.currentTarget.style.borderColor = '#e5e7eb';
                     e.currentTarget.style.transform = 'translateY(0)';
                   }}>
                <div className="flex items-center justify-between mb-3">
                  <div className="p-3 rounded-lg transition-colors" style={{backgroundColor: '#fdf2f8'}} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fce7f3'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fdf2f8'}>
                    <div style={{color: '#db2777'}}><ModernIcons.Heart size={24} /></div>
                  </div>
                  <div style={{color: '#9ca3af'}} className="transition-colors" onMouseEnter={(e) => e.currentTarget.style.color = '#4b5563'} onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}><ModernIcons.ChevronRight size={20} /></div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Здоровье</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Отслеживайте цикл, самочувствие и ведите здоровый образ жизни
                </p>
              </div>
            </Link>

            <Link to="/self-development" className="group">
              <div className="bg-white border border-gray-200 rounded-xl p-6 transition-all duration-300" 
                   style={{boxShadow: '0 2px 15px -3px rgba(0, 0, 0, 0.07), 0 10px 20px -2px rgba(0, 0, 0, 0.04)'}}
                   onMouseEnter={(e) => {
                     e.currentTarget.style.boxShadow = '0 10px 40px -10px rgba(0, 0, 0, 0.1), 0 2px 10px -2px rgba(0, 0, 0, 0.04)';
                     e.currentTarget.style.borderColor = '#d1d5db';
                     e.currentTarget.style.transform = 'translateY(-4px)';
                   }}
                   onMouseLeave={(e) => {
                     e.currentTarget.style.boxShadow = '0 2px 15px -3px rgba(0, 0, 0, 0.07), 0 10px 20px -2px rgba(0, 0, 0, 0.04)';
                     e.currentTarget.style.borderColor = '#e5e7eb';
                     e.currentTarget.style.transform = 'translateY(0)';
                   }}>
                <div className="flex items-center justify-between mb-3">
                  <div className="p-3 rounded-lg transition-colors" style={{backgroundColor: '#faf5ff'}} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3e8ff'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#faf5ff'}>
                    <div style={{color: '#9333ea'}}><ModernIcons.Sparkles size={24} /></div>
                  </div>
                  <div style={{color: '#9ca3af'}} className="transition-colors" onMouseEnter={(e) => e.currentTarget.style.color = '#4b5563'} onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}><ModernIcons.ChevronRight size={20} /></div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Развитие</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Читайте книги, изучайте новое и развивайтесь каждый день
                </p>
              </div>
            </Link>

            <Link to="/wellness" className="group">
              <div className="bg-white border border-gray-200 rounded-xl p-6 transition-all duration-300" 
                   style={{boxShadow: '0 2px 15px -3px rgba(0, 0, 0, 0.07), 0 10px 20px -2px rgba(0, 0, 0, 0.04)'}}
                   onMouseEnter={(e) => {
                     e.currentTarget.style.boxShadow = '0 10px 40px -10px rgba(0, 0, 0, 0.1), 0 2px 10px -2px rgba(0, 0, 0, 0.04)';
                     e.currentTarget.style.borderColor = '#d1d5db';
                     e.currentTarget.style.transform = 'translateY(-4px)';
                   }}
                   onMouseLeave={(e) => {
                     e.currentTarget.style.boxShadow = '0 2px 15px -3px rgba(0, 0, 0, 0.07), 0 10px 20px -2px rgba(0, 0, 0, 0.04)';
                     e.currentTarget.style.borderColor = '#e5e7eb';
                     e.currentTarget.style.transform = 'translateY(0)';
                   }}>
                <div className="flex items-center justify-between mb-3">
                  <div className="p-3 rounded-lg transition-colors" style={{backgroundColor: '#f0fdf4'}} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dcfce7'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f0fdf4'}>
                    <div style={{color: '#16a34a'}}><ModernIcons.Leaf size={24} /></div>
                  </div>
                  <div style={{color: '#9ca3af'}} className="transition-colors" onMouseEnter={(e) => e.currentTarget.style.color = '#4b5563'} onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}><ModernIcons.ChevronRight size={20} /></div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Благополучие</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Дневник настроения, медитации и работа с балансом жизни
                </p>
              </div>
            </Link>

            <Link to="/community" className="group">
              <div className="bg-white border border-gray-200 rounded-xl p-6 transition-all duration-300" 
                   style={{boxShadow: '0 2px 15px -3px rgba(0, 0, 0, 0.07), 0 10px 20px -2px rgba(0, 0, 0, 0.04)'}}
                   onMouseEnter={(e) => {
                     e.currentTarget.style.boxShadow = '0 10px 40px -10px rgba(0, 0, 0, 0.1), 0 2px 10px -2px rgba(0, 0, 0, 0.04)';
                     e.currentTarget.style.borderColor = '#d1d5db';
                     e.currentTarget.style.transform = 'translateY(-4px)';
                   }}
                   onMouseLeave={(e) => {
                     e.currentTarget.style.boxShadow = '0 2px 15px -3px rgba(0, 0, 0, 0.07), 0 10px 20px -2px rgba(0, 0, 0, 0.04)';
                     e.currentTarget.style.borderColor = '#e5e7eb';
                     e.currentTarget.style.transform = 'translateY(0)';
                   }}>
                <div className="flex items-center justify-between mb-3">
                  <div className="p-3 rounded-lg transition-colors" style={{backgroundColor: '#eef2ff'}} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0e7ff'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#eef2ff'}>
                    <div style={{color: '#4f46e5'}}><ModernIcons.User size={24} /></div>
                  </div>
                  <div style={{color: '#9ca3af'}} className="transition-colors" onMouseEnter={(e) => e.currentTarget.style.color = '#4b5563'} onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}><ModernIcons.ChevronRight size={20} /></div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Рядом Club</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Чат и форум для общения с другими участниками
                </p>
              </div>
            </Link>

            {/* Additional placeholder for better grid layout */}
            <div className="hidden lg:block"></div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default HomePage;
